import React, { useState, useRef, useEffect } from 'react';
import { MessageInputProps } from './types';
import {
  inputContainerStyles,
  inputStyles,
  sendButtonStyles,
  sendButtonDisabledStyles,
  mergeStyles,
} from './ChatWidget.styles';

/**
 * Componente de input para enviar mensagens
 */
export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  isDisabled,
  isSending,
  placeholder = 'Digite sua mensagem...',
  customStyles = {},
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Foco automático quando desbloqueia
  useEffect(() => {
    if (!isDisabled && !isSending) {
      inputRef.current?.focus();
    }
  }, [isDisabled, isSending]);

  /**
   * Envia mensagem
   */
  const handleSend = () => {
    const trimmedValue = inputValue.trim();
    
    if (!trimmedValue || isDisabled || isSending) {
      return;
    }

    onSend(trimmedValue);
    setInputValue('');
    
    // Mantém foco no input após enviar
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  /**
   * Lida com tecla Enter
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * Lida com mudança no input
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const isButtonDisabled = isDisabled || isSending || !inputValue.trim();
  const buttonStyle = mergeStyles(
    sendButtonStyles,
    isButtonDisabled
      ? { ...sendButtonDisabledStyles, ...customStyles.sendButtonDisabled }
      : customStyles.sendButton
  );

  const inputPlaceholder = isDisabled
    ? 'Aguardando resposta...'
    : isSending
    ? 'Enviando...'
    : placeholder;

  return (
    <div style={mergeStyles(inputContainerStyles, customStyles.container)}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        placeholder={inputPlaceholder}
        disabled={isDisabled || isSending}
        style={mergeStyles(inputStyles, customStyles.input)}
        aria-label="Campo de mensagem"
      />
      <button
        onClick={handleSend}
        disabled={isButtonDisabled}
        style={buttonStyle}
        aria-label="Enviar mensagem"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: 'rotate(45deg)',
            display: 'block',
          }}
        >
          <line x1="22" y1="2" x2="11" y2="13"></line>
          <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
        </svg>
      </button>
    </div>
  );
};
